import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { BankEditComponent } from 'app/common/bank/bank-edit.component';
import { BankConfiguration, BankValidationRequest, ContextVerbiage, Currency, OPERATION, SupplierBank, SupplierDocument } from 'app/profile/models/supplier.model';
import { SupplierProfileState } from 'app/profile/store/app.reducer';
import { Observable, forkJoin, of } from 'rxjs';
import * as MasterDataActions from "../../../store/actions/masterdata.action";
import * as BankDataActions from "../../../store/actions/bank.actions";
import * as MasterDataSelectors from "../../../store/selectors/masterdata.selector";
import * as BankDataSelectors from "../../../store/selectors/bank.selectors";
import * as OnboardingActions from '../../../store/actions/onboarding.actions';
import * as OnboardingSelectors from '../../../store/selectors/onboarding.selectors';
import * as LoaderActions from "../../../../shared/store/actions/loader.actions";
import * as GeneralActions from "../../../store/actions/general.action";
import * as GeneralSelectors from "../../../store/selectors/general.selector";
import { GeneralSettings, KeyValuePair } from 'app/profile/models/generic.model';
import { BankCountryConfig } from 'app/profile/models/bank.model';
import { ActivatedRoute, Router } from '@angular/router';
import { SupplierStateService } from 'app/profile/services/supplierstate.service';
import { OnboardingDocument, OnboardingTab, TaskStatus } from 'app/profile/models/onboarding.model';
import { BankUploadFileModel } from 'app/common/bank/bank-upload-file.model';
import { FileUploadService } from 'app/common/services/fileUpload.service';
import { BankService } from 'app/profile/services/bank.service';
import { TslToastrService } from '@tesla/ng-tds';
import { SupplierTaskService } from 'app/profile/services/supplier-task.service';
import { SupplierTaskDetail, SupplierTaskEntityMapping, SupplierTaskEntityMappingOption, SupplierTaskResponse, SupplierTaskStep, SupplierTaskTableCol, SupplierTaskTableMasterData } from 'app/profile/models/suppliertasks.model';
import { OnboardingState } from 'app/profile/store/state/onboarding.state';
import { ExistSelectComponent } from '../exist-select/exist-select.component';
import { MatDialog } from '@angular/material/dialog';
import { convertSupplierDocumentPropertyValuesToSupplierDocument, getSupplierBank, isNullOrEmpty } from 'app/shared/utils/utils';
import { cloneDeep } from "lodash";
import { BankTaskDialogComponent } from './bank-task-dialog/bank-task-dialog.component';
import { isNullOrUndefined } from 'util';
import { I18nService } from 'app/shared/services/i18n.service';
import { EntityMappingComponent } from '../entity-mapping/entity-mapping.component';
import { SecurityCheckComponent } from '../../bank/bank-grid/security-check/security-check.component';

@Component({
  selector: 'app-bank-task-wrapper',
  templateUrl: './bank-task-wrapper.component.html',
  styleUrls: ['./bank-task-wrapper.component.scss']
})
export class BankTaskWrapperComponent implements OnInit {
  onboardingTab: OnboardingTab = OnboardingTab.BANK;
  supplierCode: string = '';
  taskId: number;
  isLoaded: boolean = false;
  taskStatus: string;
  requestType: string;

  /** Reference of the child form component */
  @ViewChild('bankEditComponent', { static: false }) bankEditComponent: BankEditComponent;
  @ViewChild('selectTable', { static: false }) selectTableComponent: ExistSelectComponent;
  @ViewChild('entityMapping', { static: false }) entityMappingComponent: EntityMappingComponent;
  
  generalSettings$: Observable<GeneralSettings>;
  countries$: Observable<BankCountryConfig[]>;
  currencies$: Observable<Currency[]>;
  intermediaryPaymentOrigins$: Observable<KeyValuePair<string, string>[]>;
  supplierBank: SupplierBank = new SupplierBank();
  supplierBankList: SupplierBank[];
  bankConfig$: Observable<BankConfiguration>;
  requestId: string;

  requiredDocuments: OnboardingDocument;
  supplierBankDocType: string[] = ['BAC', 'CORR-B'];
  
  // reactivation
  selectedIndexList: number[] = [];
  unSelectedIndexList: number[] = [];

  primaryIndex: number;
  existingItemList: SupplierBank[];
  displayedColumns: SupplierTaskTableCol[] = [];
  tableData: SupplierTaskTableMasterData;
  isValidSelectTable: boolean = true;
  isCreateNew: boolean = false;
  tableErrorMessage: string;
  taskInfo: SupplierTaskStep;
  selectedCountry: string;
  selectedPaymentOrigin: string;
  entityMappings: SupplierTaskEntityMapping[] = [];
  entityMappingOptions: SupplierTaskEntityMappingOption[] = [];
  legalName: string;
  countryList: BankCountryConfig[];
  isSecurityCheckRequired: boolean = false;

  @Input('supplierCode') set _supplierCode(val: string) { if (val && val.trim() != "") this.supplierCode = val; };
  @Input('taskId') set _taskId(val: number) { if (val) this.taskId = val; };
  @Input('isSidePanelMode') isSidePanelMode: boolean = false;
  @Input() isReactivation: boolean = false;
  @Input('taskDetail') taskDetail: SupplierTaskDetail;
  @Input() enableExistSelection: boolean = false;
  @Output() onSave: EventEmitter<boolean> = new EventEmitter<boolean>();
  rejectionDetails: any;
  contextVerbiage: ContextVerbiage;

  constructor(private supplierStore: Store<SupplierProfileState>,
    private route: ActivatedRoute,
    private supplierStateService: SupplierStateService,
    private fileUploadService: FileUploadService,
    private bankService: BankService,
    private toaster: TslToastrService,
    private taskService: SupplierTaskService,
    private router: Router,
    private matDialog: MatDialog,
    private translate: I18nService,
    protected onboardingStore: Store<OnboardingState>
  ) { 
    this.initPaymentOriginList();
  }

  ngOnInit(): void {
    this.route?.parent?.params.subscribe(params => {
      if (params?.code && params?.portalTaskId) {
        this.supplierCode = params.code;
        this.taskId = params.portalTaskId;
      }
    });

    //  Dispatch the required actions for the data that the bank-edit component needs
    this.supplierStore.dispatch(new GeneralActions.GetGeneralSettings());
    this.supplierStore.dispatch(BankDataActions.GetBankCountries({ supplierCode: this.supplierCode }));
    this.supplierStore.dispatch(new MasterDataActions.GetCurrency());

    //  Get selectors to query the required data for the bank-edit component
    this.generalSettings$ = this.supplierStore.select(GeneralSelectors.getGeneralSettings);
    this.countries$ = this.supplierStore.select(BankDataSelectors.SelectBankCountries(this.supplierCode));
    this.countries$.subscribe(x => this.countryList = x)
    this.currencies$ = this.supplierStore.select(MasterDataSelectors.SelectCurrencies);
    this.onboardingStore.select(MasterDataSelectors.SelectContextVerbiage).subscribe(contextVerbiage => this.contextVerbiage = contextVerbiage);
    this.legalName = this.taskDetail?.legalName;

    this.taskService.getSupplierTaskStep(this.supplierCode, this.taskId, this.onboardingTab).subscribe(taskInfo => {
      if (taskInfo && taskInfo.taskConfiguration) {
        this.taskInfo = taskInfo;
        this.requestType = this.taskInfo?.taskConfiguration?.SupplierRequestType;
        this.requestId = this.taskInfo?.taskConfiguration?.SupplierRequestId;
        if(this.requestType) this.requestType = this.requestType[0].toLocaleLowerCase() + this.requestType.substring(1);
        this.taskStatus = taskInfo.taskStatus;
        this.rejectionDetails = taskInfo?.rejectionDetails;
        if (this.taskStatus != TaskStatus.Completed && taskInfo.portalTaskResponse != null) {
          if(this.isReactivation || this.enableExistSelection || ["nameOrTINChange"].includes(this.requestType)) {
            if(!taskInfo.portalTaskResponse.length)this.supplierBankList = [taskInfo.portalTaskResponse];
            else this.supplierBankList = taskInfo.portalTaskResponse.filter(x => {
              if(["nameOrTINChange"].includes(this.requestType)) return x.operation != OPERATION.DELETE;
              else return x.IsValid;
            });
          } else this.supplierBank = taskInfo.portalTaskResponse;
        }
        let doc = taskInfo.taskConfiguration?.Documents?.find(d => d.DocumentType && this.supplierBankDocType.includes(d.DocumentType.toUpperCase()));
        if (doc) {
          this.requiredDocuments = doc;
        }
        if(this.isReactivation || this.enableExistSelection || ["nameOrTINChange"].includes(this.requestType)) {
          // use supplier code and based on type to grab item list
          forkJoin([this.supplierStateService.getBankListByCode(this.supplierCode), this.supplierStateService.getDocumentsListByCode(this.supplierCode)])
          .subscribe(([resList, documentList]) => {
            this.existingItemList = resList?.map(x => getSupplierBank(x)) || [];
            this.existingItemList.forEach(x => {
              if(["nameOrTINChange"].includes(this.requestType)) {
                if(x.ID) x.TempID = x.ID;
              }
              if(x.Documents?.length) {
                let docList:SupplierDocument[] = [];
                x.Documents.forEach(docId => {
                  let doc = documentList.find(d => d.documentID.newValue == docId);
                  if(doc) docList.push(convertSupplierDocumentPropertyValuesToSupplierDocument(doc));
                });
                if(docList.length) x.DocumentInfos = docList;
                }
              if(!x.DocumentInfos?.length) x.DocumentInfos = [cloneDeep(this.requiredDocuments)]
            })
            this.initTableData();
            if(["nameOrTINChange"].includes(this.requestType)) {
              this.supplierStateService.getEntityMappingByCodeAndTaskType(this.supplierCode, "BankList").subscribe(res => {
                if(this.supplierBankList?.length) {
                  res?.forEach(x => {
                    x.taskItem = this.existingItemList.find(y => y.EntityCountryIDList?.includes(x.entityCountryID))?.TempID;
                  });
                }
                this.entityMappings = res;
                setTimeout(() => {
                  this.initEntityMappingOptionData();
                }, 0);
              });
            }
          })
        } else this.isLoaded = true;
      }

      // Update Bank Document
    });
  }

  initTableData() {
    this.displayedColumns = [
      {propertyName: "bankNickName", title: "Bank Nick Name"},
      {propertyName: "bankName", title: "Bank Name"},
      {propertyName: "bankLocalName", title: "Bank Local Name"},
      {propertyName: "last4AccountNumber", title: "Account Number"},
      {propertyName: "Last4IBANCode", title: "IBAN"},
      {propertyName: "currencyCode", title: "Currency"},
      {propertyName: "intermediaryBankName", title: "Intermediary Bank"}
    ];
    let existIndexList = [];
    if(!this.existingItemList.length) this.existingItemList = [];
    if(!this.selectedIndexList) this.selectedIndexList = [];
    if(!this.unSelectedIndexList) this.unSelectedIndexList = [];

    if(["nameOrTINChange"].includes(this.requestType)) {
      this.supplierBankList?.forEach(supplierBank => {
        if(supplierBank) {
          let existIndex = this.existingItemList?.findIndex(x => x.ID == supplierBank.ID && supplierBank.ID != 0);
          if(existIndex < 0) {
            supplierBank.Operation = OPERATION.CREATE;
            supplierBank.IsNewBankAccount = true;
            supplierBank.TempID = Math.floor(1000000 + Math.random() * 9000000);
            existIndexList.push(this.existingItemList.push(cloneDeep(supplierBank)) - 1);
          } else {
            supplierBank.TempID = supplierBank.ID;
            this.existingItemList[existIndex] = cloneDeep(supplierBank);
            if(supplierBank.Operation !== OPERATION.DELETE) existIndexList.push(existIndex)
          }
        }
      });
      let selectHeader = "Please confirm if this information is still active.";
      this.tableData = {
        supplierCode: this.supplierCode,
        onboardingTab: this.onboardingTab,
        selectHeader: selectHeader,
        countryName: this.taskInfo?.taskConfiguration?.EntityCountry,
        disableCreateNew: false,
        enableFirstEdit: true,
        disableEdit: false,
        enableMultiSelect: true,
        enablePrimarySelect: false,
        enableDocumentUpload: false,
        showMessage: true,
        enableAddNew: true,
        displayTitle: false,
        createNewLabel: "ADD NEW BANK INFORMATION",
        data: this.existingItemList?.map((x, index) => {
          let obj = {
            bankNickName: x.BankNickName,
            bankName: x.BankName,
            bankLocalName: x.BankLocalName,
            accountNumber: x.AccountNumber,
            last4AccountNumber: !isNullOrUndefined(x.Last4AccountNumber) && !isNullOrEmpty(x.Last4AccountNumber) ? 'xxxx-'+x.Last4AccountNumber : x.Last4AccountNumber,
            Last4IBANCode: !isNullOrUndefined(x.Last4IBANCode) && !isNullOrEmpty(x.Last4IBANCode) ? 'xxxx-'+x.Last4IBANCode : x.Last4IBANCode,
            currencyCode: x.CurrencyCode,
            intermediaryBankName: x.IntermediaryBankName,
            value: x.ID,
            documentInfos: x.DocumentInfos?.length? x.DocumentInfos: [cloneDeep(this.requiredDocuments)],
            isNew: x.Operation == OPERATION.CREATE
          };
          if(existIndexList?.includes(index)) {
            obj["isSelected"] = true;
            this.isValidSelectTable = true;
            this.selectedIndexList.push(index);
          } else if(existIndexList?.length || (!!(this.supplierBankList?.length  && !existIndexList?.length))) {
            this.unSelectedIndexList.push(index);
            obj["isSelected"] = false;
          }
          return obj;
        })
      };
    } else {
      if(!this.existingItemList?.length) {
        this.isValidSelectTable = true;
        this.isCreateNew = true;
      }
      this.supplierBankList?.forEach(supplierBank => {
        if(supplierBank) {
          let existIndex = this.existingItemList?.findIndex(x => x.ID == supplierBank.ID);
          if(existIndex < 0) {
            if(this.existingItemList.length) {
              this.selectedIndexList.push(this.existingItemList.length);
            }
            this.isValidSelectTable = true;
            this.isCreateNew = true;
            this.supplierBank = supplierBank;
          } else {
            this.existingItemList[existIndex] = cloneDeep(supplierBank);
            existIndexList.push(existIndex)
          }
        }
      });
  
      let selectHeader;
      if(this.requestType == "entityExtension") selectHeader = "Please confirm if this information can be used to conduct business with the listed Tesla location or add new details.";
      else selectHeader = "Please confirm if this information is still active.";
  
      this.tableData = {
        supplierCode: this.supplierCode,
        onboardingTab: this.onboardingTab,
        selectHeader: selectHeader,
        countryName: this.taskInfo?.taskConfiguration?.EntityCountry,
        disableCreateNew: false,
        enableFirstEdit: true,
        disableEdit: false,
        isNewSelected: this.supplierBankList?.length && existIndexList?.length == 0,
        enableMultiSelect: this.isReactivation,
        enablePrimarySelect: this.isReactivation,
        enableDocumentUpload: this.requestType?.toLocaleLowerCase() == "entityextension" ? false : true,
        displayTitle: true,
        showMessage: this.isReactivation,
        data: this.existingItemList?.map((x, index) => {
          let obj = {
            bankNickName: x.BankNickName,
            bankName: x.BankName,
            bankLocalName: x.BankLocalName,
            accountNumber: x.AccountNumber,
            last4AccountNumber: !isNullOrUndefined(x.Last4AccountNumber) && !isNullOrEmpty(x.Last4AccountNumber) ? 'xxxx-'+x.Last4AccountNumber : x.Last4AccountNumber,
            Last4IBANCode: !isNullOrUndefined(x.Last4IBANCode) && !isNullOrEmpty(x.Last4IBANCode) ? 'xxxx-'+x.Last4IBANCode : x.Last4IBANCode,
            currencyCode: x.CurrencyCode,
            intermediaryBankName: x.IntermediaryBankName,
            value: x.ID,
            isPrimary: x.IsPrimary,
            documentInfos: x.DocumentInfos?.length? x.DocumentInfos: [cloneDeep(this.requiredDocuments)]
          };
          if(obj.isPrimary) this.primaryIndex = index;
          if(existIndexList?.includes(index)) {
            obj["isSelected"] = true;
            this.isValidSelectTable = true;
            if(!this.selectedIndexList) this.selectedIndexList = [];
            this.selectedIndexList.push(index);
          } else if(existIndexList?.length || (!!(this.supplierBankList?.length  && !existIndexList?.length))) {
            obj["isSelected"] = false;
            if(!this.unSelectedIndexList) this.unSelectedIndexList = [];
            this.unSelectedIndexList.push(index);
          }
          return obj;
        })
      };
    }
    this.isLoaded = true;
  }

  initEntityMappingOptionData() {
    this.entityMappingOptions = this.existingItemList?.filter((x, index) => this.selectedIndexList?.includes(index))?.map(x => {
      return {
        displayName: x.BankName,
        value: x.TempID,
        info: {...x, CountryName: this.countryList?.find(y => y.abbreviation == x.CountryCode)?.name}
      }
    })
  }

  onSelected(index) {
    this.isValidSelectTable = true;
    if(["nameOrTINChange"].includes(this.requestType)) {
      if(!this.selectedIndexList) this.selectedIndexList = [];
      if(!this.unSelectedIndexList) this.unSelectedIndexList = [];
      if(index < 0) {
        if(this.selectedIndexList.includes(-(index + 1))) {
          index = -(index + 1);
          this.selectedIndexList.splice(this.selectedIndexList.indexOf(index), 1);
          this.unSelectedIndexList.push(index);
          this.initEntityMappingOptionData();
        } else this.unSelectedIndexList.push(-(index + 1));
      } else {  
        if(this.selectedIndexList.includes(this.existingItemList?.length)) {
          this.selectedIndexList.splice(this.selectedIndexList.indexOf(this.existingItemList?.length), 1);
          this.initEntityMappingOptionData();
        }
        this.selectedIndexList.push(index);
        if(this.unSelectedIndexList.includes(index)) this.unSelectedIndexList.splice(this.unSelectedIndexList.indexOf(index), 1);
      }
    } else {
      if(this.isReactivation) {
        if(!this.selectedIndexList) this.selectedIndexList = [];
        if(!this.unSelectedIndexList) this.unSelectedIndexList = [];
        if(index < 0) {
          if(this.selectedIndexList.includes(-(index + 1))) {
            index = -(index + 1);
            this.selectedIndexList.splice(this.selectedIndexList.indexOf(index), 1);
            this.unSelectedIndexList.push(index);
          }
        } else if(index == this.existingItemList?.length) {
          this.selectedIndexList = [this.existingItemList?.length];
          this.isCreateNew = true;
          this.supplierBank = new SupplierBank();
        } else {  
          if(this.selectedIndexList.includes(this.existingItemList?.length)) this.selectedIndexList.splice(this.selectedIndexList.indexOf(this.existingItemList?.length), 1);
          this.selectedIndexList.push(index);
          if(this.unSelectedIndexList.includes(index)) this.unSelectedIndexList.splice(this.unSelectedIndexList.indexOf(index), 1);
          this.isCreateNew = false;
        }
      } else {
        this.selectedIndexList = [index];
        if(this.selectedIndexList[0] >= this.existingItemList?.length) {
          this.isCreateNew = true;
          this.supplierBank = new SupplierBank();
        }
        else this.isCreateNew = false;
      }
    }
  }

  onPrimarySelected(index) {
    this.primaryIndex = index;
  }

  onEdit(event) {
    let index = event?.index;
    if(index < this.existingItemList?.length) {
      this.openBankEditForm(index, event);
    }
  }

  openBankEditForm(index: any, event: any)
  {
    const dialogRef = this.matDialog.open(BankTaskDialogComponent,
      { 
        width: '1100px', 
        maxHeight: '80vh',
        autoFocus: false,
        data: { 
          supplierCode: this.supplierCode, 
          requiredDocuments: this.requiredDocuments,
          supplierBank: cloneDeep(this.existingItemList[index]),
          disableSensitiveFieldModification: ["nameOrTINChange"].includes(this.requestType),
          generalSettings$: this.generalSettings$,
          countries$: this.countries$,
          currencies$: this.currencies$,
          bankConfig$: this.bankConfig$,
          intermediaryPaymentOrigins$: this.intermediaryPaymentOrigins$,
          showPaymentOriginControl: ["nameOrTINChange"].includes(this.requestType),
          legalName: this.legalName,
          requestType: this.requestType,
          requestId: this.requestId
        } 
      }
    );

    dialogRef.afterClosed().subscribe(res => {
      if(res) {
        if(res?.IsSecurityCheckRequired != undefined && res?.IsSecurityCheckRequired != null && res?.IsSecurityCheckRequired == true)
        {
          this.isSecurityCheckRequired = true;
        }
        else
        {
          this.isSecurityCheckRequired = (this.isSecurityCheckRequired == true) ? this.isSecurityCheckRequired : false;
        }

        res.ID = this.existingItemList[index].ID;
        res.TempID = this.existingItemList[index].TempID || this.existingItemList[index].ID;
        // res.Last4AccountNumber = res?.AccountNumber? res.AccountNumber.substr(res.AccountNumber.length - 4) : null;
        // res.Last4IBANCode = res?.IBANCode? res.IBANCode.substr(res.IBANCode.length - 4) : null;
        res.Last4IntermediaryBankAccount = res?.IntermediaryBankAccount? res.IntermediaryBankAccount.substr(res.IntermediaryBankAccount.length - 4) : null;
        res.Operation = this.existingItemList[index]?.Operation;
        this.existingItemList[index] = res;
        this.reloadTableData();
        this.initEntityMappingOptionData();
      } else {
        if(event?.isFirstTime) {
          this.selectTableComponent?.onSelectSwitch(null, index, false);
        }
      }
    })
  }

  onView(event) {
    let index = event?.index;
    if(index < this.existingItemList?.length) {
      const dialogRef = this.matDialog.open(BankTaskDialogComponent,
        { 
          width: '1100px', 
          maxHeight: '80vh',
          autoFocus: false,
          data: { 
            supplierCode: this.supplierCode, 
            requiredDocuments: this.requiredDocuments,
            supplierBank: cloneDeep(this.existingItemList[index]),
            generalSettings$: this.generalSettings$,
            countries$: this.countries$,
            currencies$: this.currencies$,
            bankConfig$: this.bankConfig$,
            readOnly: true,
            intermediaryPaymentOrigins$: this.intermediaryPaymentOrigins$,
            showPaymentOriginControl: ["nameOrTINChange"].includes(this.requestType),
            legalName: ["nameOrTINChange"].includes(this.requestType)? this.legalName : null,
            requestType: this.requestType,
            requestId: this.requestId
          } 
        }
      );
    }
  }

  onAdd() {

    // If NC / TC flow is identified and bank secuity question are not identified than we need to ask for security options
    if(this.requestType != undefined && !isNullOrEmpty(this.requestType) && this.requestType.toLowerCase() == "NameOrTINChange".toLowerCase()) {
      const securityCheckDialogRef = this.matDialog.open(SecurityCheckComponent, {
        width: '65%',
        height: '60%',
        minWidth: '770px',
        data: {
          supplierCode: this.supplierCode,
          requestType: this.requestType,
          onCloseExecuteNotAnimation: true
        }
      }); 

      securityCheckDialogRef.afterClosed().subscribe((securityAnswers: any) => {
        if (securityAnswers != undefined && securityAnswers == true) {
          this.openAddBankTask();
        }
      },((err) => {
        this.router.navigate([`${this.supplierCode}`, "Onboarding", "Home" , this.taskId])
        .then(() => {
          window.location.reload();
        })
      }));
    }
    else
    {
      this.openAddBankTask();
    }
  }
  

  openAddBankTask()
  {
    const dialogRef = this.matDialog.open(BankTaskDialogComponent,
      { 
        width: '1100px', 
        maxHeight: '80vh',
        autoFocus: false,
        data: { 
          supplierCode: this.supplierCode, 
          requiredDocuments: this.requiredDocuments,
          generalSettings$: this.generalSettings$,
          supplierBank: new SupplierBank(),
          countries$: this.countries$,
          currencies$: this.currencies$,
          bankConfig$: this.bankConfig$,
          intermediaryPaymentOrigins$: this.intermediaryPaymentOrigins$,
          showPaymentOriginControl: ["nameOrTINChange"].includes(this.requestType),
          legalName: this.legalName,
          requestType: this.requestType,
          requestId: this.requestId
        } 
      }
    );
    dialogRef.afterClosed().subscribe(res => {
      if(res) {
        res.TempID = Math.floor(1000000 + Math.random() * 9000000);
        res.Operation = OPERATION.CREATE;
        res.IsNewBankAccount = true;
        // res.Last4AccountNumber = res?.AccountNumber? res.AccountNumber.substr(res.AccountNumber.length - 4) : null;
        // res.Last4IBANCode = res?.IBANCode? res.IBANCode.substr(res.IBANCode.length - 4) : null;
        res.Last4IntermediaryBankAccount = res?.IntermediaryBankAccount? res.IntermediaryBankAccount.substr(res.IntermediaryBankAccount.length - 4) : null;
        this.selectedIndexList.push(this.existingItemList.push(cloneDeep(res)) - 1);
        this.reloadTableData();
        this.initEntityMappingOptionData();
      }
    });
  }

  onDelete(event) {
    let index = event?.index;
    this.selectedIndexList.splice(this.selectedIndexList.indexOf(index), 1);
    this.existingItemList.splice(index, 1);
    this.reloadTableData();
    this.initEntityMappingOptionData();
  }

  onFileUpload(event) {
    if(event) {
      this.existingItemList[event.index].DocumentInfos[event.documentIndex] = event.file;
    }
  }

  reloadTableData() {
    this.selectTableComponent?.updateTableData(this.existingItemList?.map((x, i) => {
      let obj = {
        bankNickName: x.BankNickName,
        bankName: x.BankName,
        bankLocalName: x.BankLocalName,
        accountNumber: x.AccountNumber,
        last4AccountNumber: !isNullOrUndefined(x.Last4AccountNumber) && !isNullOrEmpty(x.Last4AccountNumber) ? 'xxxx-'+x.Last4AccountNumber : x.AccountNumber,
        Last4IBANCode: !isNullOrUndefined(x.Last4IBANCode) && !isNullOrEmpty(x.Last4IBANCode) ? 'xxxx-'+x.Last4IBANCode : x.Last4IBANCode,
        currencyCode: x.CurrencyCode,
        intermediaryBankName: x.IntermediaryBankName,
        value: x.ID,
        isPrimary: i == this.primaryIndex,
        documentInfos: x.DocumentInfos?.length? x.DocumentInfos: [cloneDeep(this.requiredDocuments)],
        isNew: ["nameOrTINChange"].includes(this.requestType) ? x.Operation == OPERATION.CREATE : null
      };
      if(this.selectedIndexList?.includes(i)) {
        obj["isSelected"] = true;
        this.isValidSelectTable = true;
      } else {
        if(["nameOrTINChange"].includes(this.requestType)) {
          if(this.unSelectedIndexList?.includes(i)) obj["isSelected"] = false;
        } else if(this.selectedIndexList?.length || (!!(this.supplierBankList?.length  && !this.selectedIndexList?.length))) obj["isSelected"] = false;
      }
      return obj;
    }), !["nameOrTINChange"].includes(this.requestType))
  }

  /** Requests bank configuration each time the country chang */
  countryChangeEventHandler(params: { countryCode: string }) {
    //  Get the bank configuration for the selected country
    this.selectedCountry = params.countryCode;
    this.bankConfig$ = this.supplierStateService.getBankConfigurationForOnboarding(this.supplierCode, params.countryCode, this.requestId);
  }

  onPaymentOriginChangedEvent(params: { paymentOrigin: string }) {
    this.selectedPaymentOrigin = params.paymentOrigin;
    this.bankConfig$ = this.supplierStateService.getBankConfiguration(this.selectedCountry, this.selectedPaymentOrigin);
  }

  /** Triggers submit action of the bank edit child component */
  onSubmit(isPartialSave?: boolean) {
    this.existingItemList?.forEach(x => {
      if(!x.DocumentInfos?.length) x.DocumentInfos = null;
      else x.DocumentInfos = x.DocumentInfos.filter(x => !!x);
    })
    if(["nameOrTINChange"].includes(this.requestType)) {
      if(!this.selectedIndexList?.length) {
        this.isValidSelectTable = false; 
        this.tableErrorMessage = "Please confirm at least one active bank above or add new information."
        return;
      }
      if(this.selectedIndexList?.length + this.unSelectedIndexList?.length != this.existingItemList?.length) {
        this.isValidSelectTable = false; 
        this.tableErrorMessage = "Please make decision for all items."
        return;
      }
      let entityMapping = this.entityMappingComponent?.onSubmit();
      if(!entityMapping?.length) return;
      let submitList = this.existingItemList?.filter((x, index) => this.selectedIndexList.findIndex(x => x == index) >= 0)?.map((item, index) => {
        item.EntityCountryIDList = entityMapping.filter(x => x.taskItem == item.TempID)?.map(x => x.entityCountryID);
        if(item.Operation != OPERATION.CREATE) item.Operation = OPERATION.UPDATE;
        delete item.TempID;
        return item;
      });
      
      if(this.isSecurityCheckRequired == true)
      {
        const securityCheckDialogRef = this.matDialog.open(SecurityCheckComponent, {
          width: '65%',
          height: '60%',
          minWidth: '770px',
          data: {
            supplierCode: this.supplierCode,
            requestType: this.requestType,
            onCloseExecuteNotAnimation: true
          }
        }); 

        securityCheckDialogRef.afterClosed().subscribe((securityAnswers: any) => {
          if (securityAnswers != undefined && securityAnswers == true) {
            this.submitEventHandler(null, null, false, submitList, null);
          }
          else
          {
            if(securityAnswers != undefined && securityAnswers == false)
            {
              this.toaster.error(this.translate.get('Bank Security Questions not correctly answered.'));
            }
          }
        }, ((err) => {
          this.router.navigate([`${this.supplierCode}`, "Onboarding", "Home" , this.taskId])
          .then(() => {
            window.location.reload();
          })
        }));
      }
      else
      {
        this.submitEventHandler(null, null, false, submitList, null);
      }
    } else {
      if(this.isReactivation || this.enableExistSelection) {
        if(this.existingItemList?.length && !this.selectedIndexList?.length) {
          this.isValidSelectTable = false; 
          this.tableErrorMessage = "Please confirm at least one active bank above or add new information."
          return;
        }
        if(this.isReactivation && !this.isCreateNew && !this.primaryIndex && this.primaryIndex != 0){
          this.isValidSelectTable = false; 
          this.tableErrorMessage = "Please select at least one primary bank."
          return;
        }
        if(!this.isCreateNew) {
          let submitList = this.selectedIndexList.map(index => {
            let item = this.existingItemList[index];
            item.IsValid = true;
            if(index == this.primaryIndex) item.IsPrimary = true;
            else item.IsPrimary = false;
            return item;
          })
          if(submitList?.findIndex(x => (x.SkipDocumentValidation == null || x.SkipDocumentValidation == false) && (!x?.DocumentInfos?.length || !x.DocumentInfos[0].EffectiveDate || !(x.DocumentInfos[0].File || x.DocumentInfos[0].DocumentID))) > -1) {
            this.isValidSelectTable = false; 
            this.tableErrorMessage = "Please provide required document and effective date."
            return;
          }
          this.submitEventHandler(null, null, false, submitList, null);
          return;
        }
      }
      this.bankEditComponent.onSubmit(isPartialSave);
    }
  }

  verifyEventHandler(supplierBank: SupplierBank) {
    this.bankService.getVerificationKeys(this.supplierCode)
      .subscribe((response) => {
        var argsAcc = this.bankService.getVerifyArgs(response.signInToken, response.instrumentReference);
        this.bankEditComponent.payoutRefAngular.verifyAccount(argsAcc)
          .then(res => {
            if (res.decision == 'ACCEPT') {
              // we are receiving bpm events for deposit confirmation so skipping the UI call.
              // this.onCompleteEvent.emit();
              this.supplierBank.VerificationStatus = 'VERIFIED';
              if (this.isSidePanelMode) this.onSave.emit(true);
            }
          })
          .catch(ex => {
            this.toaster.error(this.translate.get('Something went wrong!'));
          })
      });
  }

  submitBankForm(supplierBank: SupplierBank, uploadFiles: BankUploadFileModel[], supplierBankList?: SupplierBank[]) {
    //  Send validation request
    if(!supplierBankList?.length) {
      this.sendValidationRequest(supplierBank)
      .pipe()

      //  Validation request returns a collection of errors if there are any.
      //  If we don't have any error, upload the files to the server,
      //  If we do have errors, then set the error message.
      .mergeMap(errors => {
        if (!errors || errors.length <= 0) {
          let filesToUpload = uploadFiles.filter(uf => uf.fileData != null);
          let existingFiles = uploadFiles.filter(uf => uf.fileData == null)?.map(file => file.supplierDocument);

          if (filesToUpload.length > 0) {

            //  Upload files
            return this.fileUploadService.uploadFileToServer(filesToUpload.map(uf => uf.fileData), this.supplierCode)
              .map(newFiles => {
                return [newFiles, ...existingFiles]
              });
          } else {

            //  Return existing files as an observable
            return of(existingFiles);
          }
        }

        //  Othersise set error message and return null
        this.supplierStore.dispatch(new LoaderActions.DecrementLoaderCount());
        return of(null);
      })

      //  Once, the upload file action is complete, then send post to save step status
      .map(resp => {
        if (resp) {

          //  Cast the response as a collection of supplier documents
          let docs = Array.isArray(resp) ? resp as SupplierDocument[] : [resp as SupplierDocument];

          //  Append the list of document IDs to the supplier bank
          supplierBank.Documents = docs.map(doc => doc['documentID'] || doc.DocumentID);
          supplierBank.DocumentInfos = docs.map(doc => ({
            DocumentID: doc['documentID'] || doc.DocumentID,
            FileName: doc['fileName'] || doc.FileName,
            DocumentVersion: doc['documentVersion'] || doc.DocumentVersion,
            UploadedDate: doc['modifiedDate'] || doc.ModifiedDate,
            Mode: 'Upload',
            TypeID: this.requiredDocuments?.TypeID,
            DocumentType: this.requiredDocuments?.DocumentType ?? doc.DocumentType,
            DocumentTypeDescription: this.requiredDocuments?.DocumentTypeDescription,
          }));

          //  Map the effective and expiry dates
          supplierBank.DocumentInfos.forEach(doc => {
            let file = uploadFiles.find(file => file.fileData?.name == doc.FileName || file.supplierDocument?.FileName == doc.FileName);

            if (file && file.effectiveDateControl) {
              doc.EffectiveDate = file.effectiveDateControl.value;
            } else {
              doc.EffectiveDate = doc['effectiveDate'] || doc.EffectiveDate;
            }

            if (file && file.expireDateControl) {
              doc.ExpiryDate = file.expireDateControl.value;
            } else {
              doc.ExpiryDate = doc['expiryDate'] || doc.ExpiryDate;
            }
          });
        }
      }, err => this.toaster.error(this.translate.get('Something went wrong!')))

      //  Subscribe so that we can capture an error if any
      .subscribe(() => { }, err => this.toaster.error(this.translate.get('Something went wrong!')));
    } else {
      supplierBankList?.forEach(bank => {
        this.sendValidationRequest(bank)
      .pipe()

      //  Validation request returns a collection of errors if there are any.
      //  If we don't have any error, upload the files to the server,
      //  If we do have errors, then set the error message.
      .mergeMap(errors => {
        if (!errors || errors.length <= 0) {
          let filesToUpload = uploadFiles.filter(uf => uf.fileData != null);
          let existingFiles = uploadFiles.filter(uf => uf.fileData == null)?.map(file => file.supplierDocument);

          if (filesToUpload.length > 0) {

            //  Upload files
            return this.fileUploadService.uploadFileToServer(filesToUpload.map(uf => uf.fileData), this.supplierCode)
              .map(newFiles => {
                return [newFiles, ...existingFiles]
              });
          } else {

            //  Return existing files as an observable
            return of(existingFiles);
          }
        }

        //  Othersise set error message and return null
        this.supplierStore.dispatch(new LoaderActions.DecrementLoaderCount());
        return of(null);
      })

      //  Once, the upload file action is complete, then send post to save step status
      .map(resp => {
        if (resp) {

          //  Cast the response as a collection of supplier documents
          let docs = Array.isArray(resp) ? resp as SupplierDocument[] : [resp as SupplierDocument];

          //  Append the list of document IDs to the supplier bank
          bank.Documents = docs.map(doc => doc['documentID'] || doc.DocumentID);
          bank.DocumentInfos = docs.map(doc => ({
            DocumentID: doc['documentID'] || doc.DocumentID,
            FileName: doc['fileName'] || doc.FileName,
            DocumentVersion: doc['documentVersion'] || doc.DocumentVersion,
            UploadedDate: doc['modifiedDate'] || doc.ModifiedDate,
            Mode: 'Upload',
            TypeID: this.requiredDocuments?.TypeID,
            DocumentType: this.requiredDocuments?.DocumentType ?? doc.DocumentType,
            DocumentTypeDescription: this.requiredDocuments?.DocumentTypeDescription,
          }));

          //  Map the effective and expiry dates
          bank.DocumentInfos.forEach(doc => {
            let file = uploadFiles.find(file => file.fileData?.name == doc.FileName || file.supplierDocument?.FileName == doc.FileName);

            if (file && file.effectiveDateControl) {
              doc.EffectiveDate = file.effectiveDateControl.value;
            } else {
              doc.EffectiveDate = doc['effectiveDate'] || doc.EffectiveDate;
            }

            if (file && file.expireDateControl) {
              doc.ExpiryDate = file.expireDateControl.value;
            } else {
              doc.ExpiryDate = doc['expiryDate'] || doc.ExpiryDate;
            }
          });
        }
      }, err => this.toaster.error(this.translate.get('Something went wrong!')))

      //  Subscribe so that we can capture an error if any
      .subscribe(() => { }, err => this.toaster.error(this.translate.get('Something went wrong!')));
      })
    }
    

    // TODO: Submit data

    if (supplierBank || supplierBankList?.length) {
      let body: SupplierBank | SupplierBank[];
      if(this.isReactivation || this.enableExistSelection) {
        if(!supplierBankList?.length) {
          supplierBank.IsPrimary = true;
          if(this.isReactivation) body = [{ ...supplierBank }];
          else body = { ...supplierBank };
        }
        else {
          if(this.isReactivation) body = supplierBankList;
          else body = {...supplierBankList[0]};
        }
      } else body = { ...supplierBank };
      var response: SupplierTaskResponse = {
        id: this.taskId,
        supplierCode: this.supplierCode,
        taskResponseJson: JSON.stringify(body)
      }

      this.taskService.postSupplierTaskStepResponse(this.supplierCode, response).subscribe(res => {
        if (res) {
          this.toaster.success(this.translate.get('Submitted successfully!'));
          if (this.isSidePanelMode) this.onSave.emit(true);
          else this.router.navigate([`${this.supplierCode}`, "Onboarding", "Home"]);
        }
        else {
          this.toaster.error(this.translate.get('Something went wrong!'));
        }
      });
    }
  }

  /** Sends validation request to see if the bank data is valid */
  private sendValidationRequest(bank: SupplierBank): Observable<string[]> {
    //  SEND VALIDATION REQUEST ABOUT THE BANK DATA
    const validationRequest = new BankValidationRequest();
    validationRequest.bankCountryId = bank.CountryCode;
    //validationRequest.paymentOrigin = supplierBank.pay;   //  There's no payment origin in onboarding

    validationRequest.isNewBankAccount = bank.IsNewBankAccount;
    validationRequest.bankAccountId = !bank.StageID && (!bank.AccountNumberExists || bank.IsNewBankAccount) ? bank.AccountNumber : null;
    validationRequest.bankIBAN = !bank.StageID && (!bank.IBANCodeExists || bank.IsNewBankAccount) ? bank.IBANCode : null;

    validationRequest.bankSwift = bank.SwiftCode;
    validationRequest.bankRoutingCode = bank.ABANumber ? bank.ABANumber.toString() : '';

    validationRequest.beneficiaryNameEnglish = bank.BeneficiaryNameEnglish;
    validationRequest.beneficiaryNameLocalLanguage = bank.BeneficiaryNameLocal;

    validationRequest.paymentType = bank.PaymentType;
    validationRequest.bankAccountKatanaka = bank.BankAccountNameKatanaka;

    //  Address fields
    validationRequest.streetAddressLine1 = bank.Address1;
    validationRequest.streetAddressLine2 = bank.Address2;
    validationRequest.city = bank.City;
    validationRequest.state = bank.State;
    validationRequest.zipCode = bank.ZipCode?.toString();
    validationRequest.id = bank.ID;
    validationRequest.verificationStatus = bank.VerificationStatus;
    validationRequest.verificationTransactionId = bank.VerificationTransactionId;

    this.supplierStore.dispatch(new LoaderActions.IncrementLoaderCount());
    return this.supplierStateService.validateBankConfigurationWithoutOTP(this.supplierCode, validationRequest)
  }

  setErrorMessage(message: string) {
    if (message) this.toaster.error((message && message.trim() != '') ? this.translate.get(message) : this.translate.get('Something went wrong!'))
  }

  submitEventHandler(supplierBank: SupplierBank, uploadFiles: BankUploadFileModel[], isPartialSave: boolean, supplierBankList?: SupplierBank[], isBankDataChanged?: boolean) {
    if(!supplierBankList?.length) {
      //  Send validation request
      this.sendValidationRequest(supplierBank)
        // .pipe(takeUntil(this.ngUnsubscribe))

        //  Validation request returns a collection of errors if there are any.
        //  If we don't have any error, upload the files to the server,
        //  If we do have errors, then set the error message.
        .mergeMap(errors => {
          if (!errors || errors.length <= 0) {
            let filesToUpload = uploadFiles.filter(uf => uf.fileData != null);
            let existingFiles = uploadFiles.filter(uf => uf.fileData == null)?.map(file => file.supplierDocument);

            if (filesToUpload.length > 0) {

              //  Upload files
              return this.fileUploadService.uploadFileToServer(filesToUpload.map(uf => uf.fileData), this.supplierCode)
                .map(newFiles => {
                  return [newFiles, ...existingFiles]
                });
            } else {

              //  Return existing files as an observable
              return of(existingFiles);
            }
          }

          //  Othersise set error message and return null
          this.setErrorMessage(errors.join('; '));
          this.supplierStore.dispatch(new LoaderActions.DecrementLoaderCount());
          return of(null);
        })

        //  Once, the upload file action is complete, then send post to save step status
        .map(resp => {
          if (resp) {

            //  Cast the response as a collection of supplier documents
            let docs = Array.isArray(resp) ? resp as SupplierDocument[] : [resp as SupplierDocument];

            //  Append the list of document IDs to the supplier bank
            supplierBank.Documents = docs.map(doc => doc['documentID'] || doc.DocumentID);
            supplierBank.DocumentInfos = docs.map(doc => ({
              DocumentID: doc['documentID'] || doc.DocumentID,
              FileName: doc['fileName'] || doc.FileName,
              DocumentVersion: doc['documentVersion'] || doc.DocumentVersion,
              UploadedDate: doc['modifiedDate'] || doc.ModifiedDate,
              Mode: 'Upload',
              TypeID: this.requiredDocuments?.TypeID,
              DocumentType: this.requiredDocuments?.DocumentType ?? doc.DocumentType,
              DocumentTypeDescription: this.requiredDocuments?.DocumentTypeDescription,
            }));

            //  Map the effective and expiry dates
            supplierBank.DocumentInfos.forEach(doc => {
              let file = uploadFiles.find(file => file.fileData?.name == doc.FileName || file.supplierDocument?.FileName == doc.FileName);

              if (file && file.effectiveDateControl) {
                doc.EffectiveDate = file.effectiveDateControl.value;
              } else {
                doc.EffectiveDate = doc['effectiveDate'] || doc.EffectiveDate;
              }

              if (file && file.expireDateControl) {
                doc.ExpiryDate = file.expireDateControl.value;
              } else {
                doc.ExpiryDate = doc['expiryDate'] || doc.ExpiryDate;
              }
            });

            //  Dispatch action to save the bank details
            //  There's a listener in the init action that will invoke the oncomplete event when
            //  a new post success action is dispatched.
            let body: SupplierBank | SupplierBank[];
            if(this.isReactivation || this.enableExistSelection) {
              supplierBank.IsPrimary = true;
              supplierBank.IsValid = true;
              let notValidList = cloneDeep(this.existingItemList);
              notValidList.forEach(x => {
                x.IsValid = false;
                x.IsPrimary = false;
                x.DocumentInfos = [];
                x.Documents = [];
              });
              if(this.isReactivation) body = [{ ...supplierBank }, ...notValidList];
              else body = { ...supplierBank };
            } else body = { ...supplierBank }
            var response: SupplierTaskResponse = {
              id: this.taskId,
              supplierCode: this.supplierCode,
              taskResponseJson: JSON.stringify(body),
              action: isPartialSave ? 'Save' : 'Submit'
            }
            this.taskService.postSupplierTaskStepResponse(this.supplierCode, response).subscribe(res => {
              if (res) {
                this.toaster.success(this.translate.get('Submitted successfully!'));
                if (this.isSidePanelMode) this.onSave.emit(true);
                else this.router.navigate([`${this.supplierCode}`, "Onboarding", "Home"]);
              }
              else {
                this.toaster.error(this.translate.get('Something went wrong!'));
              }
            },
              err => this.toaster.error(this.translate.get('Something went wrong!')));
          }
        }, err => this.setErrorMessage(err))

        //  Subscribe so that we can capture an error if any
        .subscribe(() => { }, err => this.setErrorMessage(err));

    } else {
      let validationObsArray:Observable<string[]>[] = [];
      supplierBankList.forEach(x => validationObsArray.push(this.sendValidationRequest(x)));
      forkJoin(validationObsArray).subscribe(errors => {
        let hasError: boolean = false;
        let errorList = [];
        if(errors.length) {
          errors.forEach(error => {
            if(error?.length) {
              hasError = true;
              errorList.push(error.join("; "));
            }
          });
        }
        // display errors
        if(hasError) {
          this.setErrorMessage(errorList.join('; '));
          this.supplierStore.dispatch(new LoaderActions.DecrementLoaderCount());
          return;
        }
        let uploadFileObsList: Observable<any>[] = [];
        let uploadFileIndexList: number[] = [];
        supplierBankList.forEach((bank, index) => {
          if(bank.DocumentInfos?.length) {
            if(bank.DocumentInfos[0].File && !bank.DocumentInfos[0].DocumentID) {
              uploadFileObsList.push(this.fileUploadService.uploadFileToServer([bank.DocumentInfos[0].File], this.supplierCode));
              uploadFileIndexList.push(index);
            }
          }
        });
        if(uploadFileObsList.length) {
          forkJoin(uploadFileObsList).subscribe(files => {
            if(files?.length) {
              files.forEach((file, index) => {
                let document = supplierBankList[uploadFileIndexList[index]].DocumentInfos[0];
                document.DocumentID = file['documentID'] || file.DocumentID;
                document.FileName = file['fileName'] || file.FileName;
                document.DocumentVersion = file['documentVersion'] || file.DocumentVersion;
                document.UploadedDate = file['modifiedDate'] || file.ModifiedDate;
                document.Mode = 'Upload';
                document.TypeID = this.requiredDocuments?.TypeID;
                document.DocumentType = this.requiredDocuments?.DocumentType ?? file.DocumentType;
                document.DocumentTypeDescription = this.requiredDocuments?.DocumentTypeDescription;
                document.File = null;
                supplierBankList[uploadFileIndexList[index]].Documents = supplierBankList[uploadFileIndexList[index]].DocumentInfos?.map(x => x.DocumentID);
              })
            }
            this.postBankListTask(supplierBankList, isPartialSave);
          }, error => this.toaster.error(this.translate.get('Something went wrong!')))
        } else this.postBankListTask(supplierBankList, isPartialSave);
      }, error => this.toaster.error(this.translate.get('Something went wrong!')))
    }
  }

  postBankListTask(supplierBankList: SupplierBank[], isPartialSave: boolean) {
    // delete empty documents array
    supplierBankList.forEach(bank => {
      bank.DocumentInfos = bank.DocumentInfos?.filter(x => x.DocumentID != null);
    })
    let notValidList = this.existingItemList?.filter((x, index) => !this.selectedIndexList.includes(index)) || [];
    notValidList.forEach(x => {
      x.IsValid = false;
      x.IsPrimary = false;
      x.DocumentInfos = [];
      x.Documents = [];
      x.Operation = OPERATION.DELETE;
    });
    let body: SupplierBank | SupplierBank[];
    if(this.isReactivation || ["nameOrTINChange"].includes(this.requestType)) body = [...cloneDeep(supplierBankList), ...notValidList];
    else body = {...supplierBankList[0]};
    var response: SupplierTaskResponse = {
      id: this.taskId,
      supplierCode: this.supplierCode,
      taskResponseJson: JSON.stringify(body),
      action: isPartialSave ? 'Save' : 'Submit'
    }
    this.taskService.postSupplierTaskStepResponse(this.supplierCode, response).subscribe(res => {
      if (res) {
        this.toaster.success(this.translate.get('Submitted successfully!'));
        if (this.isSidePanelMode) this.onSave.emit(true);
        else this.router.navigate([`${this.supplierCode}`, "Onboarding", "Home"]);
      }
      else {
        this.toaster.error(this.translate.get('Something went wrong!'));
      }
    },
      err => this.toaster.error(this.translate.get('Something went wrong!')));
  }

  saveEventHandler(bank: SupplierBank) {

  }

  initPaymentOriginList() {
    let Domestic = new KeyValuePair<string, string>();
    Domestic.Value = "Domestic";
    Domestic.Key = this.translate.get("Domestic");
    let CrossBorder = new KeyValuePair<string, string>();
    CrossBorder.Value = "CrossBorder";
    CrossBorder.Key = this.translate.get("CrossBorder");
    let Both = new KeyValuePair<string, string>();
    Both.Value = "Both";
    Both.Key = this.translate.get("Both");
    this.intermediaryPaymentOrigins$ = of([Domestic, CrossBorder, Both]);
  }
}
